import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { fmtDate, PageHead, V3Page } from '~/components/cockpit/v3';
import {
  DOCUMENT_TYPE_LABELS,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
)({
  component: DocumentsPage,
});

interface Doc {
  readonly id?: string;
  readonly type: string;
  readonly title?: string;
  readonly filename?: string;
  readonly url?: string;
  readonly uploadedAt?: string;
  readonly isPrimary?: boolean;
}

function DocumentsPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = (query.data?.widgetProps || {}) as Record<
      string,
      Record<string, unknown>
    >;
    const widgets = (
      query.data?.widgets || [
        {
          name: 'cockpitDocumentsWidget',
          type: 'cockpitDocumentsWidget',
          cols: 12,
        },
      ]
    ).map((widget: { name: string; type: string; cols: number }) => ({
      ...widget,
      props: widgetProps[widget.name] || {},
    }));

    return (
      <div className="container mx-auto py-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const w =
    ((query.data.widgetProps || {}) as WidgetProps).cockpitDocumentsWidget ??
    {};
  const docs: Doc[] = w.documents ?? [];
  const missing: string[] = w.missingDocumentTypes ?? [];
  const required: string[] = w.requiredDocumentTypes ?? [];
  const label = (type: string, fallback?: string): string =>
    DOCUMENT_TYPE_LABELS[type] ?? fallback ?? type;
  const primary = docs.filter((d) => d.isPrimary);
  const other = docs.filter((d) => !d.isPrimary);

  const row = (d: Doc, i: number): React.ReactElement => (
    <div
      className="doc"
      key={d.id ?? `${d.type}-${i}`}
    >
      <div className="bd">
        <span className="t">{label(d.type, d.title)}</span>
        <span className="fn">
          {d.filename}
          {d.uploadedAt ? ` · ${fmtDate(d.uploadedAt)}` : ''}
        </span>
      </div>
      {d.url ? (
        <div className="end">
          <a
            className="btn"
            href={d.url}
            target="_blank"
            rel="noreferrer"
          >
            {t('cockpit_docs_open', 'Öffnen')}
          </a>
        </div>
      ) : null}
    </div>
  );

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_docs_title', 'Dokumente')}
        sub={t('cockpit_docs_sub', 'Alle Unterlagen zu Ihrer Immobilie.')}
      />
      {missing.length ? (
        <div
          className="callout"
          role="note"
        >
          <strong>{t('cockpit_docs_missing', 'Fehlende Dokumente')}:</strong>{' '}
          {missing.map((m) => label(m)).join(', ')}
        </div>
      ) : null}
      <div className="cols">
        <section
          className="card"
          data-widget="cockpitDocumentsWidget"
        >
          {docs.length === 0 ? (
            <p className="mu">
              {t('cockpit_docs_empty', 'Noch keine Dokumente vorhanden.')}
            </p>
          ) : (
            <>
              {primary.length ? (
                <div className="dg">
                  <h3>{t('cockpit_docs_marketing', 'Vermarktung')}</h3>
                  {primary.map(row)}
                </div>
              ) : null}
              {other.length ? (
                <div className="dg">
                  <h3>{t('cockpit_docs_other', 'Unterlagen')}</h3>
                  {other.map(row)}
                </div>
              ) : null}
            </>
          )}
        </section>
        <aside className="side">
          {required.length ? (
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_docs_checklist', 'Checkliste')}</h2>
              </div>
              {required.map((r) => {
                const ok = !missing.includes(r);
                return (
                  <div
                    className={`ck ${ok ? 'ok' : 'miss'}`}
                    key={r}
                  >
                    <span
                      className="b"
                      aria-hidden="true"
                    >
                      {ok ? '✓' : ''}
                    </span>
                    <span>{label(r)}</span>
                    <span className="sr">
                      {ok
                        ? t('cockpit_docs_present', 'vorhanden')
                        : t('cockpit_docs_absent', 'fehlt')}
                    </span>
                  </div>
                );
              })}
            </section>
          ) : null}
        </aside>
      </div>
    </V3Page>
  );
}
